import { BusinessPlanSection, GenerationContext, SectionId, SECTION_METADATA, SectionStatus } from '../types/businessPlan';
import { ENV } from '../config/env';
import { supabase } from '../lib/supabase';

interface RequestOptions {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: any;
}

export class AnthropicService {
  // Get the authentication token from Supabase
  private async getToken(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token || '';
    } catch (e) {
      console.error('Error getting Supabase auth token:', e);
      return '';
    }
  }

  private async makeRequest<T>(options: RequestOptions): Promise<T> {
    const { endpoint, method = 'POST', data } = options;
    const url = `${ENV.API_URL}${endpoint}`;

    try {
      console.log(`Making ${method} request to ${url}`);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getToken()}`
        },
        body: data ? JSON.stringify(data) : undefined,
      });

      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response as JSON:', responseText);
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${responseText}`);
      }
      
      if (!response.ok) {
        console.error('API request failed:', responseData);
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${JSON.stringify(responseData)}`);
      }

      return responseData;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  async generateCompletePlan(context: GenerationContext): Promise<Record<SectionId, BusinessPlanSection>> {
    // Ensure the businessIdea has all required properties
    if (!context.businessIdea) {
      throw new Error('Business idea is required for plan generation');
    }

    console.log('Generating business plan sections individually...');
    
    // Get the ordered list of sections to generate
    const sectionIds = Object.keys(SECTION_METADATA) as SectionId[];
    
    // Initialize result with empty sections to satisfy TypeScript
    const result: Record<SectionId, BusinessPlanSection> = {} as Record<SectionId, BusinessPlanSection>;
    
    // Generate each section individually
    for (const sectionId of sectionIds) {
      try {
        console.log(`Generating section: ${sectionId}`);
        const section = await this.generateSingleSection(sectionId, context);
        result[sectionId] = section;
      } catch (error) {
        console.error(`Error generating section ${sectionId}:`, error);
        // Create a placeholder for the failed section
        result[sectionId] = {
          id: sectionId,
          title: SECTION_METADATA[sectionId].title,
          content: `Error generating content for ${SECTION_METADATA[sectionId].title}. Please regenerate this section.`,
          status: 'error',
          pageNumber: SECTION_METADATA[sectionId].pageNumber || 0
        };
      }
    }
    
    return result;
  }
  
  private async generateSingleSection(sectionId: SectionId, context: GenerationContext): Promise<BusinessPlanSection> {
    // Format the request data to match backend expectations
    const requestData = {
      sectionId,
      context: {
        businessIdea: {
          title: context.businessIdea.title,
          description: context.businessIdea.description,
          potentialMarket: context.businessIdea.potentialMarket,
          skillsUtilized: context.businessIdea.skillsUtilized,
          id: context.businessIdea.id,
          createdAt: context.businessIdea.createdAt
        }
      }
    };

    // Make the API request
    const section = await this.makeRequest<BusinessPlanSection>({
      endpoint: '/api/business-plans/generate-section',
      data: requestData,
    });
    
    // Ensure the section has a pageNumber
    if (!section.pageNumber) {
      section.pageNumber = SECTION_METADATA[sectionId].pageNumber || 0;
    }
    
    return section;
  }

  async regenerateSection(sectionId: SectionId, context: GenerationContext): Promise<BusinessPlanSection> {
    // Format the request data to match backend expectations
    const requestData = {
      sectionId,
      context: {
        businessIdea: {
          title: context.businessIdea.title,
          description: context.businessIdea.description,
          potentialMarket: context.businessIdea.potentialMarket,
          skillsUtilized: context.businessIdea.skillsUtilized,
          id: context.businessIdea.id,
          createdAt: context.businessIdea.createdAt,
          industry: context.businessIdea.industry
        },
        additionalData: context.additionalData,
        sections: context.sections || SECTION_METADATA
      }
    };

    console.log(`Regenerating section ${sectionId}:`, JSON.stringify(requestData, null, 2));

    try {
      // Add a timestamp to prevent duplicate requests
      const timestamp = new Date().getTime();
      const response = await fetch(`${ENV.API_URL}/business-plan/regenerate-section?t=${timestamp}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getToken()}`
        },
        body: JSON.stringify(requestData)
      });

      // Check if the response is ok
      if (!response.ok) {
        // Try to get error details from the response
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = errorData.error || '';
        } catch (e) {
          // If we can't parse the error, just use the status text
          errorDetails = response.statusText;
        }

        const errorMessage = `Failed to regenerate section: ${errorDetails} (${response.status})`;
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Validate the response data
      if (!data || !data.content) {
        console.error('Invalid response data:', data);
        throw new Error('Invalid response from server');
      }

      // Create a properly formatted section object
      const updatedSection: BusinessPlanSection = {
        id: sectionId,
        title: data.title || SECTION_METADATA[sectionId].title,
        content: data.content,
        status: 'completed' as SectionStatus,
        pageNumber: SECTION_METADATA[sectionId].pageNumber,
        subsections: data.subsections || []
      };

      return updatedSection;
    } catch (error) {
      console.error('Error regenerating section:', error);
      // Return a section with error status
      return {
        id: sectionId,
        title: SECTION_METADATA[sectionId].title,
        content: '',
        status: 'error' as SectionStatus,
        pageNumber: SECTION_METADATA[sectionId].pageNumber,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async validateSection(section: BusinessPlanSection): Promise<{ isValid: boolean; feedback?: string }> {
    // Format the request data to match backend expectations
    const requestData = {
      section
    };

    console.log(`Validating section ${section.id}:`, JSON.stringify(requestData, null, 2));

    // Make the API request
    return this.makeRequest<{ isValid: boolean; feedback?: string }>({  
      endpoint: '/api/business-plans/validate-section',
      data: requestData,
    });
  }

  async generateText(prompt: string): Promise<string> {
    try {
      // Validate the prompt
      if (!prompt || prompt.trim() === '') {
        throw new Error('Prompt is required for text generation');
      }

      console.log('Generating text with Anthropic...');
      
      // Format the request data
      const requestData = {
        prompt,
        max_tokens: 1000,
        temperature: 0.7,
      };

      // Make the API request
      const response = await this.makeRequest<{ text: string }>({
        endpoint: '/api/anthropic/generate',
        method: 'POST',
        data: requestData,
      });

      return response.text;
    } catch (error) {
      console.error('Error generating text:', error);
      throw error;
    }
  }
}

// Create a singleton instance of the AnthropicService
export const anthropicService = new AnthropicService();
